import { useQuery } from '@apollo/client';
import { Boxes, ChevronDown, ChevronUp, Pill, ScanBarcode, Stethoscope } from 'lucide-react';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BarcodeScanBox } from './BarcodeScanBox';
import { PREVIEW_UNIT_PRICES_QUERY, SEARCH_SELLABLE_QUERY } from './doctor-pos.gql';

import { SELLABLE_KIND_FROM_GQL } from '@/shared/graphql/enums';
import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';

export interface SellableHit {
  kind: string;
  id: string;
  code: string;
  name: string;
  description: string | null;
  price_cents: string;
  stock_qty: string | null;
  stock_min: string | null;
  is_exception_handle: boolean;
  is_quick_key: boolean;
  tax_rate_id: string | null;
  category_id: string | null;
  available: boolean;
  partial_kind?: string | null;
  requires_current_weight?: boolean;
  resolved_unit_price_cents?: string | null;
  resolved_price_rule_id?: string | null;
  price_missing_required_context?: string[] | null;
}

export interface PreviewContext {
  species?: string | null;
  sex?: string | null;
  breed?: string | null;
  current_weight_grams?: number | null;
  approximate_age_months?: number | null;
  branch_id?: string | null;
  customer_id?: string | null;
}

interface SellableSearchPanelProps {
  onAdd: (hit: SellableHit, quantity: string) => void | Promise<void>;
  disabled?: boolean;
  /** Limit visible result kinds when a consumer (e.g. cashier POS) needs a narrower catalog. */
  allowed_kinds?: ('product' | 'service' | 'kit')[];
  /** Hide technical/internal services that should not be sold directly. */
  hide_internal_services?: boolean;
  /** When supplied, the panel fetches `previewUnitPrices` for the result set so the
   *  doctor sees the rule-resolved price BEFORE committing the line. */
  preview_context?: PreviewContext | null;
  /** Hide the barcode-scan toggle/box (e.g. cashier POS doesn't need it for now). */
  hide_barcode_scan?: boolean;
}

interface PreviewRow {
  subject_kind: string;
  subject_id: string;
  unit_price_cents: string | null;
  price_rule_id: string | null;
  fallback_used: boolean;
  missing_required_context: string[] | null;
}

interface PreviewResponse {
  previewUnitPrices: PreviewRow[];
}

interface SearchSellableResponse {
  searchSellable: SellableHit[];
}

function toPriceRuleSubjectKindGql(kind: string): 'PRODUCT' | 'SERVICE' | 'KIT' | null {
  switch (kind) {
    case 'product':
      return 'PRODUCT';
    case 'service':
      return 'SERVICE';
    case 'kit':
      return 'KIT';
    default:
      return null;
  }
}

const MIN_SEARCH_TERM_LENGTH = 2;

export const SellableSearchPanel = forwardRef<HTMLInputElement, SellableSearchPanelProps>(
  function SellableSearchPanel(
    {
      onAdd,
      disabled,
      allowed_kinds,
      hide_internal_services = false,
      preview_context,
      hide_barcode_scan = false,
    },
    search_input_ref,
  ): JSX.Element {
    const { t } = useTranslation();
    const [term, setTerm] = useState('');
    const [debounced, setDebounced] = useState('');
    const [highlight_index, setHighlightIndex] = useState(0);
    const [show_barcode, setShowBarcode] = useState(false);

    useEffect(() => {
      const handle = window.setTimeout(() => setDebounced(term.trim()), 200);
      return () => window.clearTimeout(handle);
    }, [term]);

    const has_min_search_term = debounced.length >= MIN_SEARCH_TERM_LENGTH;

    const { data: results_data, loading } = useQuery<SearchSellableResponse>(
      SEARCH_SELLABLE_QUERY,
      {
        variables: { input: { term: debounced, limit: 30 } },
        skip: !has_min_search_term,
        fetchPolicy: 'cache-and-network',
      },
    );
    const { data: quick_data } = useQuery<SearchSellableResponse>(SEARCH_SELLABLE_QUERY, {
      variables: { input: { quick_key_only: true, limit: 6 } },
      fetchPolicy: 'cache-first',
    });

    const results = useMemo(() => {
      if (!has_min_search_term) return [];
      const normalized = normalizeSellableHits(results_data?.searchSellable ?? []);
      return normalized.filter((hit) => {
        if (
          allowed_kinds &&
          allowed_kinds.length > 0 &&
          !allowed_kinds.includes(hit.kind as 'product' | 'service' | 'kit')
        ) {
          return false;
        }
        if (!hide_internal_services) return true;
        return !isInternalService(hit);
      });
    }, [results_data, allowed_kinds, hide_internal_services, has_min_search_term]);
    const quick_keys = useMemo(
      () => normalizeSellableHits(quick_data?.searchSellable ?? []),
      [quick_data],
    );

    const preview_subjects = useMemo(() => {
      const seen = new Set<string>();
      return [...results, ...quick_keys]
        .filter((r) => r.kind && r.id)
        .filter((r) => {
          const key = `${r.kind}:${r.id}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((r) => {
          const subject_kind = toPriceRuleSubjectKindGql(r.kind);
          return subject_kind ? { subject_kind, subject_id: r.id } : null;
        })
        .filter(
          (value): value is { subject_kind: 'PRODUCT' | 'SERVICE' | 'KIT'; subject_id: string } =>
            value !== null,
        );
    }, [results, quick_keys]);
    const { data: preview_data } = useQuery<PreviewResponse>(PREVIEW_UNIT_PRICES_QUERY, {
      variables: { subjects: preview_subjects, context: preview_context ?? null },
      skip: !preview_context || preview_subjects.length === 0,
      fetchPolicy: 'cache-and-network',
    });
    const price_overrides = useMemo(() => {
      const map = new Map<string, PreviewRow>();
      for (const row of preview_data?.previewUnitPrices ?? []) {
        map.set(`${normalizeSubjectKind(row.subject_kind)}:${row.subject_id}`, row);
      }
      return map;
    }, [preview_data]);

    useEffect(() => {
      setHighlightIndex(0);
    }, [debounced]);

    const resolveHitForAdd = (hit: SellableHit): SellableHit => {
      const override = price_overrides.get(`${hit.kind}:${hit.id}`);
      if (!override) return hit;
      if (override.missing_required_context && override.missing_required_context.length > 0) {
        return {
          ...hit,
          price_missing_required_context: override.missing_required_context,
        };
      }
      return {
        ...hit,
        resolved_unit_price_cents: override.unit_price_cents ?? hit.price_cents,
        resolved_price_rule_id: override.price_rule_id,
      };
    };

    const onClickAdd = async (hit: SellableHit): Promise<void> => {
      if (!hit.available) return;
      await onAdd(resolveHitForAdd(hit), '1');
    };

    const onKeyDownSearch = async (event: React.KeyboardEvent<HTMLInputElement>): Promise<void> => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightIndex((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightIndex((i) => Math.max(i - 1, 0));
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        const hit = results[highlight_index];
        if (hit) await onClickAdd(hit);
      }
    };

    return (
      <div className="space-y-3">
        {hide_barcode_scan ? null : (
          <div className="border-input bg-muted/30 rounded-md border border-dashed">
            <button
              type="button"
              className="text-muted-foreground flex w-full items-center justify-between gap-2 px-3 py-2 text-xs font-medium"
              onClick={() => setShowBarcode((v) => !v)}
              aria-expanded={show_barcode}
            >
              <span className="flex items-center gap-2">
                <ScanBarcode className="h-4 w-4" aria-hidden />
                {show_barcode ? t('doctor_pos.barcode_hide') : t('doctor_pos.barcode_hidden_message')}
              </span>
              {show_barcode ? (
                <ChevronUp className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronDown className="h-4 w-4" aria-hidden />
              )}
            </button>
            {show_barcode ? (
              <div className="border-input border-t px-3 py-2">
                <BarcodeScanBox
                  disabled={disabled}
                  on_add={(hit) => {
                    const [normalized] = normalizeSellableHits([hit]);
                    if (normalized) void onClickAdd(normalized);
                  }}
                />
              </div>
            ) : null}
          </div>
        )}
        <Input
          ref={search_input_ref}
          type="search"
          placeholder={t('doctor_pos.search_placeholder')}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            void onKeyDownSearch(e);
          }}
          disabled={disabled}
        />

        {quick_keys.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {quick_keys.map((hit) => (
              <Button
                key={hit.id}
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  void onClickAdd(hit);
                }}
                disabled={disabled === true || !hit.available}
                title={hit.name}
              >
                <Stethoscope className="h-4 w-4" aria-hidden />
                <span className="max-w-[10rem] truncate">{hit.name}</span>
              </Button>
            ))}
          </div>
        ) : null}

        <ul role="listbox" className="max-h-[60vh] space-y-1 overflow-x-hidden overflow-y-auto pr-1">
          {!has_min_search_term ? (
            <li className="text-muted-foreground px-3 py-4 text-center text-sm">
              {t('doctor_pos.search_min_chars_hint')}
            </li>
          ) : loading && results.length === 0 ? (
            <li className="text-muted-foreground px-3 py-2 text-xs">{t('common.loading')}</li>
          ) : null}
          {results.map((hit, idx) => {
            const is_highlight = idx === highlight_index;
            return (
              <li
                key={`${hit.kind}:${hit.id}`}
                role="option"
                aria-selected={is_highlight}
                className={`flex items-center gap-3 rounded-md border ${
                  is_highlight ? 'border-primary bg-primary/5' : 'border-transparent'
                } px-3 py-2 text-sm`}
              >
                <KindIcon kind={hit.kind} />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{hit.name}</p>
                  <div className="text-muted-foreground flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-mono">{hit.code}</span>
                    <KindBadge kind={hit.kind} />
                    {hit.partial_kind === 'parent_with_doses' ? (
                      <Badge variant="outline">{t('doctor_pos.dose_badge')}</Badge>
                    ) : null}
                    {hit.partial_kind === 'fractional_qty' ? (
                      <Badge variant="outline">{t('doctor_pos.fractional_badge')}</Badge>
                    ) : null}
                    {hit.requires_current_weight ? (
                      <Badge variant="outline" className="border-primary text-primary">
                        {t('doctor_pos.requires_weight_badge')}
                      </Badge>
                    ) : null}
                    {hit.is_quick_key ? <Badge variant="muted">F</Badge> : null}
                    {!hit.available ? (
                      <Badge variant="outline" className="border-destructive text-destructive">
                        {t('doctor_pos.unavailable')}
                      </Badge>
                    ) : hit.stock_qty !== null ? (
                      <span>
                        {t('doctor_pos.stock_short')}: {hit.stock_qty}
                      </span>
                    ) : null}
                  </div>
                </div>
                <span className="font-mono text-sm">
                  {(() => {
                    const override = price_overrides.get(`${hit.kind}:${hit.id}`);
                    if (
                      override?.missing_required_context &&
                      override.missing_required_context.length > 0
                    ) {
                      return (
                        <span className="text-primary text-xs">
                          {t('doctor_pos.requires_weight_badge')}
                        </span>
                      );
                    }
                    const resolved = override?.unit_price_cents ?? hit.price_cents;
                    const ruled = override?.price_rule_id ? '·★' : '';
                    return `${formatCentsString(resolved)}${ruled}`;
                  })()}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="default"
                  onClick={() => {
                    void onClickAdd(hit);
                  }}
                  disabled={disabled === true || !hit.available}
                >
                  {t('doctor_pos.add')}
                </Button>
              </li>
            );
          })}
          {results.length === 0 && !loading && has_min_search_term ? (
            <li className="text-muted-foreground px-3 py-4 text-center text-sm">
              {t('doctor_pos.no_results')}
            </li>
          ) : null}
        </ul>
      </div>
    );
  },
);

function normalizeSellableHits(items: SellableHit[]): SellableHit[] {
  return items.map((item) => ({
    ...item,
    kind: SELLABLE_KIND_FROM_GQL[item.kind as keyof typeof SELLABLE_KIND_FROM_GQL] ?? item.kind,
  }));
}

function normalizeSubjectKind(kind: string): string {
  return (
    SELLABLE_KIND_FROM_GQL[kind as keyof typeof SELLABLE_KIND_FROM_GQL] ?? kind
  ).toLowerCase();
}

function KindIcon({ kind }: { kind: SellableHit['kind'] }): JSX.Element {
  if (kind === 'service') return <Stethoscope className="text-primary h-4 w-4" aria-hidden />;
  if (kind === 'kit') return <Boxes className="text-primary h-4 w-4" aria-hidden />;
  return <Pill className="text-primary h-4 w-4" aria-hidden />;
}

function KindBadge({ kind }: { kind: SellableHit['kind'] }): JSX.Element {
  const { t } = useTranslation();
  return <Badge variant="outline">{t(`doctor_pos.kind_${kind ?? 'product'}`)}</Badge>;
}

function isInternalService(hit: SellableHit): boolean {
  if (hit.kind !== 'service') return false;
  if (hit.code.startsWith('SRV-QX-')) return true;
  const description = hit.description?.toLowerCase() ?? '';
  if (description.includes('no se vende por separado')) return true;
  return false;
}
